import type { ListBuilderConfiguration } from '../types';

/**
 * Inputs to `createSourcingRequestEmbeddedConfig`. Each parent-entity SR
 * embedded config differs only in id / name / list-builder cache key — the
 * column layout, sort, paging, and filter set are shared.
 */
interface SourcingRequestEmbeddedFactoryParams {
	/** Unique list id (e.g. `'default-sourcing-requests-client-embedded'`). */
	id: string;
	/** List display name shown in the embedded list header (e.g. `'Client Sourcing Requests'`). */
	name: string;
}

/**
 * Build a Sourcing Request embedded list configuration scoped to a parent
 * entity (Client / Vendor / Item). The actual parent filter is supplied at
 * runtime by the consuming tab component (`additionalContext` /
 * `queryFn` filter merging) — this factory only specifies the column
 * layout, click action, and the registry of filters that the user can
 * adjust on top of the locked parent filter.
 *
 * Columns (left → right):
 *   - Status (badge with the locked enum-driven label set)
 *   - Request name + type badge
 *   - Priority (badge)
 *   - Lines progress (linesFulfilled / totalLines)
 *   - Target date
 *
 * Click action navigates into the SR detail. Pagination defaults to 25.
 * Allowed filters mirror the main SR list minus the parent-scope filters
 * (`sourcingRequestClient` is excluded from the client-embedded view since
 * it is already locked; the consuming tab can choose to suppress others
 * via list config when needed).
 */
export function createSourcingRequestEmbeddedConfig(
	params: SourcingRequestEmbeddedFactoryParams,
): ListBuilderConfiguration {
	return {
		lists: [
			{
				id: params.id,
				cacheTTL: 300000,
				name: params.name,
				type: 'default',
				active: true,
				sortOrder: 0,
				columns: [
					{
						id: 'sr-status',
						fields: [
							{
								id: 'status-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'status',
										objectTable: 'sourcing_request',
									},
								},
								displayStyle: 'badge',
								displayConfig: {
									valueMapping: [
										{
											value: 'draft',
											displayValue: 'Draft',
											variant: 'secondary',
										},
										{ value: 'open', displayValue: 'Open', variant: 'info' },
										{
											value: 'in_progress',
											displayValue: 'In Progress',
											variant: 'warning',
										},
										{
											value: 'partially_fulfilled',
											displayValue: 'Partial',
											variant: 'warning',
										},
										{
											value: 'fulfilled',
											displayValue: 'Fulfilled',
											variant: 'success',
										},
										{
											value: 'cancelled',
											displayValue: 'Cancelled',
											variant: 'danger',
										},
									],
								},
							},
						],
						label: 'Status',
						headers: [{ id: 'h-status', text: 'Status' }],
						styleConfig: { size: 'md', colorRule: { type: 'default' } },
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'status',
								objectTable: 'sourcing_request',
							},
						],
						width: 'w-28',
					},
					{
						id: 'sr-name-type',
						fields: [
							{
								id: 'name-field',
								link: {
									type: 'object',
									enabled: true,
									objectTable: 'sourcing_request',
								},
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'name',
										objectTable: 'sourcing_request',
									},
									defaultValue: '-',
								},
								styleConfig: {
									size: 'md',
									weight: 'medium',
									colorRule: { type: 'default' },
								},
								displayStyle: 'text',
							},
							{
								id: 'line-break-1',
								dataConfig: { type: 'custom_text', customText: '' },
								styleConfig: { size: 'md', colorRule: { type: 'default' } },
								displayStyle: 'line_break',
							},
							{
								id: 'type-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'type',
										objectTable: 'sourcing_request',
									},
									defaultValue: '-',
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'badge',
								displayConfig: {
									valueMapping: [
										{ value: 'quote', displayValue: 'Quote', variant: 'info' },
										{
											value: 'order',
											displayValue: 'Order',
											variant: 'success',
										},
									],
								},
							},
						],
						label: 'Request',
						headers: [{ id: 'h-name', text: 'Request' }],
						styleConfig: { size: 'md', colorRule: { type: 'default' } },
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'name',
								objectTable: 'sourcing_request',
							},
						],
						width: 'w-40',
					},
					{
						id: 'sr-priority',
						fields: [
							{
								id: 'priority-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'priority',
										objectTable: 'sourcing_request',
									},
								},
								displayStyle: 'badge',
								displayConfig: {
									valueMapping: [
										{
											value: 'low',
											displayValue: 'Low',
											variant: 'secondary',
										},
										{
											value: 'normal',
											displayValue: 'Normal',
											variant: 'info',
										},
										{
											value: 'high',
											displayValue: 'High',
											variant: 'warning',
										},
										{
											value: 'urgent',
											displayValue: 'Urgent',
											variant: 'danger',
										},
									],
								},
							},
						],
						label: 'Priority',
						headers: [{ id: 'h-priority', text: 'Priority' }],
						styleConfig: { size: 'md', colorRule: { type: 'default' } },
						sortable: true,
						sortFields: [
							{
								direction: 'DESC',
								fieldName: 'priority',
								objectTable: 'sourcing_request',
							},
						],
						width: 'w-24',
					},
					{
						id: 'sr-lines',
						fields: [
							{
								id: 'lines-count-field',
								dataConfig: {
									type: 'template',
									template: '{linesFulfilled}/{totalLines}',
								},
								styleConfig: { size: 'sm', colorRule: { type: 'default' } },
								displayStyle: 'text',
							},
						],
						label: 'Lines',
						headers: [{ id: 'h-lines', text: 'Lines' }],
						styleConfig: { size: 'md', colorRule: { type: 'default' } },
						sortable: true,
						sortFields: [
							{
								direction: 'DESC',
								fieldName: 'total_lines',
								objectTable: 'sourcing_request',
							},
						],
						width: 'w-20',
					},
					{
						id: 'sr-target-date',
						fields: [
							{
								id: 'target-date-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'targetDate',
										objectTable: 'sourcing_request',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
									formatOptions: { dateFormat: 'short' },
								},
								displayStyle: 'date',
							},
						],
						label: 'Target',
						headers: [{ id: 'h-target', text: 'Target' }],
						styleConfig: { size: 'md', colorRule: { type: 'default' } },
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'targetDate',
								objectTable: 'sourcing_request',
							},
						],
						width: 'w-28',
					},
				],
				defaultSortId: 'column-sr-priority-desc',
				clickAction: {
					type: 'navigate',
					path: '/sourcing-requests/{id}',
				},
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [10, 25, 50, 100],
		},
		showResetButton: true,
		filters: {
			activeFilters: [
				'sourcingRequestKeywords',
				'sourcingRequestStatus',
				'sourcingRequestType',
				'sourcingRequestPriority',
				'sourcingRequestLineStatus',
			],
			defaultFilters: {},
		},
		objects: [
			{
				baseUrl: '/sourcing-requests/:id',
				objectLabel: 'Sourcing Request',
				objectTable: 'sourcing_request',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{ fieldName: 'name', fieldLabel: 'Name' },
					{ fieldName: 'status', fieldLabel: 'Status' },
					{ fieldName: 'type', fieldLabel: 'Type' },
					{ fieldName: 'priority', fieldLabel: 'Priority' },
					{ fieldName: 'target_date', fieldLabel: 'Target Date' },
					{ fieldName: 'total_lines', fieldLabel: 'Total Lines' },
					{ fieldName: 'lines_fulfilled', fieldLabel: 'Lines Fulfilled' },
					{ fieldName: '_created_date', fieldLabel: 'Created Date' },
					{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
				],
			},
		],
	};
}
